import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Mui
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { Box } from "@mui/system";
import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

// Redux
import { useDispatch } from "react-redux";
import { register, login } from "../../../store/reducers/reducerUser";

// Auth
import Joi from "joi";

// Images
import bk from "../../../assets/imgs/class/bk.gif";
import sm from "../../../assets/imgs/class/sm.gif";
import elf from "../../../assets/imgs/class/elf.gif";
import mg from "../../../assets/imgs/class/mg.gif";
import dl from "../../../assets/imgs/class/dl.gif";

import "./signup.scss";

import SiteButton from "../../ui/SiteButton";
import { useLang } from "../../../i18n/LangContext";

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLang();

  const [state, setState] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    selectImg: "",
  });

  const [formSend, setFormSend] = useState(false);
  const [loader, setLoader] = useState(false);
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoader(true);

    if (!acceptedRules) {
      setLoader(false);
      return toast.error("You must accept the rules to register");
    }

    const { username, password, confirmPassword, email, selectImg } = state;

    try {
      const data = { username, password, email, selectImg };
      const { error } = Joi.object({ ...schema }).validate(state, {
        abortEarly: false,
      });

      if (error) {
        setLoader(false);
        for (const details of error.details) {
          return toast.error(details.message);
        }
        return null;
      }

      if (!password.includes(confirmPassword)) {
        setLoader(false);
        return toast.error("Password and confirm password do not match");
      }

      dispatch(register(data)).then(function (response) {
        const { error } = response;

        if (error) {
          setFormSend(false);
          setLoader(false);
          return toast.error(
            `The username or email you entered already exists`,
          );
        }

        let userForLogin = {
          username,
          password,
        };

        setLoader(false);

        dispatch(login(userForLogin)).then(function () {
          navigate("/profile");
        });
      });
    } catch (error) {
      if (error) {
        setLoader(false);
        return (
          toast.error("Something went wrong, please try again"),
          setFormSend(false)
        );
      }
    }
  }

  useEffect(() => {}, [state]);

  const classes = useStyles();

  return (
    <div className="mu-signup">
      <h1 className="mu-signup__title">{t("registration")}</h1>

      <Box id="containerSignUp" className="mu-signup__box">
        <Box className="containerTitleSignUp">
          <div id="containerTitleSignUp">
            <span className="mu-signup__subtitle borderTitle">
              {t("choose_profile_picture")}
            </span>
            <div className="subContainerSignUpTitle">
              {[bk, sm, elf, mg, dl].map((img, index) => (
                <img
                  key={index}
                  className={
                    state.selectImg === index + 1
                      ? "mu-signup__avatar is-selected"
                      : "mu-signup__avatar"
                  }
                  onClick={() =>
                    setState({
                      ...state,
                      selectImg: index + 1,
                    })
                  }
                  src={img}
                  alt=""
                />
              ))}
            </div>
          </div>
        </Box>

        <Box
          id="subContainerSignUp"
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <TextField
            size="small"
            sx={{ marginBottom: "15px", width: "min(420px, 92%)" }}
            className={classes.root}
            label={t("username")}
            value={state.username}
            onChange={(e) => setState({ ...state, username: e.target.value })}
          />
          <TextField
            sx={{ marginBottom: "15px", width: "min(420px, 92%)" }}
            className={classes.root}
            size="small"
            label={t("password")}
            value={state.password}
            type="password"
            onChange={(e) => setState({ ...state, password: e.target.value })}
          />
          <TextField
            sx={{ marginBottom: "15px", width: "min(420px, 92%)" }}
            className={classes.root}
            size="small"
            label={t("confirm_password")}
            value={state.confirmPassword}
            type="password"
            onChange={(e) =>
              setState({ ...state, confirmPassword: e.target.value })
            }
          />
          <TextField
            sx={{ marginBottom: "15px", width: "min(420px, 92%)" }}
            className={classes.root}
            size="small"
            label={t("email_address")}
            value={state.email}
            type="email"
            onChange={(e) => setState({ ...state, email: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedRules}
                onChange={(e) => setAcceptedRules(e.target.checked)}
                sx={{
                  color: "var(--mu-gold)",
                  "&.Mui-checked": { color: "var(--mu-gold-bright)" },
                }}
              />
            }
            label={
              <span className="mu-signup__agree">
                {t("agree_rules_prefix")}{" "}
                <button
                  type="button"
                  className="mu-signup__rules-link"
                  onClick={() => setRulesOpen(true)}
                >
                  {t("server_rules_link")}
                </button>
              </span>
            }
          />
          <Dialog
            open={rulesOpen}
            onClose={() => setRulesOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                background: "linear-gradient(180deg, #1a1018 0%, #0c0810 100%)",
                color: "var(--mu-text)",
                border: "1px solid rgba(212, 175, 55, 0.45)",
              },
            }}
          >
            <DialogTitle>
              <h1 className="mu-signup__rules-title">
                {t("server_rules_title")}
              </h1>
            </DialogTitle>
            <DialogContent>
              <pre className="mu-signup__rules-body">{t("rules_body")}</pre>
            </DialogContent>
          </Dialog>
          {loader ? (
            <CircularProgress
              sx={{ color: "var(--mu-gold)", margin: "20px 0" }}
            />
          ) : null}

          <SiteButton type="submit" variant="primary" disabled={formSend}>
            {t("create_account")}
          </SiteButton>
        </Box>
      </Box>
    </div>
  );
};

const schema = {
  username: Joi.string().min(3).max(10).required(),
  password: Joi.string().required().min(3).max(10).label("Password"),
  confirmPassword: Joi.string().min(3).max(10).required(),
  email: Joi.string()
    .required()
    .email({
      tlds: { allow: false },
    })
    .label("Email"),
  selectImg: Joi.number().min(1).max(5).required().messages({
    "number.base": `You need to select an image`,
  }),
};

const useStyles = makeStyles({
  root: {
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(212, 175, 55, 0.55)",
    },
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--mu-gold)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--mu-gold-bright)",
    },
    "& .MuiOutlinedInput-input": {
      color: "var(--mu-text)",
    },
    "&:hover .MuiOutlinedInput-input": {
      color: "var(--mu-text)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
      color: "var(--mu-text)",
    },
    "& .MuiInputLabel-outlined": {
      color: "var(--mu-text-muted)",
    },
    "&:hover .MuiInputLabel-outlined": {
      color: "var(--mu-gold)",
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "var(--mu-gold-bright)",
    },
    "& .MuiOutlinedInput-root": {
      background: "rgba(12, 8, 18, 0.75)",
    },
  },
});

export default SignUp;
